<template>
    <v-row class="mx-2">
        <v-col cols=12 md=6>
            <div v-if="requests.length">
                <h2>{{$translate('Applications')}}</h2>
                <v-card v-for="request in requests" :key="request.id" class="ma-4 pa-4" @click="answer(request)">
                    <div class="d-flex">
                        <user-avatar :size="50" :user="request.requester" />
                        <div class="flex-grow-1 ml-2">
                            <div class="font-weight-bold">{{request.requester.name}}</div>
                            {{request.text}}
                        </div>
                        <v-btn text color="primary" @click="answer(request)">{{$translate('Answer')}}</v-btn>
                    </div>
                </v-card>
            </div>
            <div v-if="sentRequests.length">
                <h2>{{$translate('Waiting for a reply')}}</h2>
                <v-card v-for="request in sentRequests" :key="request.id" class="ma-4 pa-4">
                    <div class="d-flex">
                        <user-avatar :size="50" :user="request.user" />
                        <div class="flex-grow-1 ml-2">
                            <div class="font-weight-bold">{{request.user.name}}</div>
                            {{request.text}}
                        </div>
                        <v-btn text color="primary" @click="cancelRequest(request)">{{$translate('Remove')}}</v-btn>
                    </div>
                </v-card>
            </div>
            <h2>{{$translate('Family')}}</h2>
            <div class="text--secondary">{{$translate('Your family members')}}</div>
            <v-card v-for="user in users" :key="user.id" class="ma-4 pa-4">
                <v-row>
                    <v-col class="d-flex align-center">
                        <user-avatar :size="50" :user="user" />
                        <div class="flex-grow-1 ml-2">
                            <div class="font-weight-bold">{{ user.name }}</div>
                        </div>
                        <v-btn text color="primary" @click="change(user)">
                            {{$translate('Change')}}
                        </v-btn>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <div>{{ user.relationText }}</div>
                    </v-col>
                </v-row>
                <template v-if="user.manage">
                    <div v-for="block in user.blocks" :key="block.id">
                        <v-alert
                            dense
                            text
                            type="error"
                            class="mt-1">
                            <span v-safe-html="getBlockMessage(block)" />
                        </v-alert>
                        <v-btn
                            v-if="block.type == 'warning'"
                            text
                            light
                            class="red--text ml-11"
                            @click="showWarningsDialog = true; currentUser = user;"
                        >
                            {{$translate('Show warnings')}}
                        </v-btn>
                        <v-btn
                            v-if="block.type == 'warning' && $store.state.settings.blockFeeProductId"
                            text
                            light
                            class="ml-4 red--text"
                            @click="showBlockFeeDialog = true; currentUser = user; "
                        >
                            {{$translate('Lift the block')}}
                        </v-btn>
                    </div>
                </template>

                <div v-if="userHasWarningsButIsNotBlocked(user)">
                    <div v-if="user.manage && !user.warnedUntil && user.warnings" icon="mdi-sticker-alert-outline" icon-color="error">
                        <v-btn
                            class="ml-11"
                            text
                            color="red lighten-1"
                            light
                            @click="showWarningsDialog = true; currentUser = user; "
                        >
                            {{$translate('Show warnings')}}
                        </v-btn>
                    </div>
                </div>
            </v-card>
            <div v-if="users.length == 0" class="mt-4">
                {{$translate('Your family members will show up here. Add someone.')}}
            </div>
        </v-col>
        <v-col cols=12 md=6>
            <h2>{{$translate('Add family members')}}</h2>
            <div class="text--secondary">{{$translate('The more the merrier!')}}</div>
            <v-btn color="primary" class="mt-4" large @click="add">{{$translate('Add family member')}}</v-btn>
        </v-col>
        <v-dialog v-model="showRequestDialog" v-if="request" max-width="500px">
            <v-card>
                <v-card-title class="headline flex-column pa-2">
                    <div class="fill-width">
                        {{$translate('Application')}}
                        <v-btn icon @click="showRequestDialog = false" class="float-right">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </div>
                </v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col cols=4>
                            <user-avatar :size="100" :user="request.requester" />
                        </v-col>
                        <v-col cols=8>
                            <div class="font-weight-bold">{{$translate('Application from')}} {{request.requester.name}}</div>
                            <div>{{request.text}}</div>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        class="zoezi-button-thin"
                        color="primary"
                        outlined
                        @click="declineRequest(request)"
                        :loading="declining"
                        :disabled="accepting"
                    >
                        {{$translate('Decline')}}
                    </v-btn>
                    <v-spacer />
                    <v-btn
                        class="zoezi-button-thin"
                        color="primary"
                        @click="acceptRequest(request)"
                        :loading="accepting"
                        :disabled="declining"
                    >
                        {{$translate('Approve')}}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog v-if="showBlockFeeDialog && canBuyBuyBlockFee" v-model="showBlockFeeDialog" scrollable max-width="850px" :fullscreen="$vuetify.breakpoint.mobile">
            <v-card tile elevation="0" class="z-no-x-scroll">
                <v-card-title class="headline pa-2 primary primarytext--text d-flex fill-width">
                    <v-spacer />
                    <v-btn icon @click="showBlockFeeDialog = false">
                        <v-icon class="primary primarytext--text">mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <zoezi-checkout class="zoezi-checkout" :items="[{product_id: $store.state.settings.blockFeeProductId, user_id: currentUser.id}]" />
            </v-card>
        </v-dialog>
        <v-dialog v-if="showWarningsDialog" v-model="showWarningsDialog" scrollable max-width="850px">
            <v-card tile elevation="0" class="z-no-x-scroll">
                <v-card-title class="headline pa-2 primary primarytext--text d-flex fill-width">
                    <v-icon class="primary primarytext--text mr-2">mdi-sticker-alert-outline</v-icon>
                    {{$translate('Warnings')}}
                    <v-spacer />
                    <v-btn icon @click="showWarningsDialog = false">
                        <v-icon class="primary primarytext--text">mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text>
                    <div class="font-weight-bold mt-4">{{currentUser.name.split(' ')[0]}} {{$translate('has been warned on the following occasions')}}</div>
                    <ul v-if="warnings">
                        <li v-for="warning in warnings" :key="warning.id">
                            <span class="font-weight-bold">{{Date.newFull(warning.created).yyyymmdd()}}</span>
                            <span class="ml-2">{{warning.reason}}</span>
                        </li>
                    </ul>
                </v-card-text>
                <v-card-actions>
                    <v-btn v-if="canBuyBuyBlockFee"
                           class="zoezi-button-thin"
                           color="primary"
                           block
                           @click="showBlockFeeDialog = true;"
                    >
                        {{$translate('Lift the block')}}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-row>
</template>


<script>
import UserAvatar from '../components/UserAvatar.vue'
import { BLOCK_TYPES } from '../services/BlockTypes';
export default {
    name: 'zoezi-family',

    components: {
        UserAvatar
    },

    data: () => ({
        showRequestDialog: false,
        request: null,
        accepting: false,
        declining: false,
        showBlockFeeDialog: false,
        showWarningsDialog: false,
        warningsRaw: null,
        currentUser: null
    }),
    watch:{
        currentUser: {
            immediate: true,
            handler(currentUser) {
                this.warningsRaw = null;
                if (currentUser) {
                    this.getWarnings();
                }
            }
        },
    },
    computed:{
        requests() {
            let me = this.$store.state.user;
            if (!me) {
                return [];
            }

            return me.requests.map(r => Object.assign({}, r, {text: this.getRequestText(r)}));
        },
        sentRequests() {
            let me = this.$store.state.user;
            if (!me) {
                return [];
            }

            return me.requestssent.map(r => Object.assign({}, r, {text: this.getSentRequestText(r)}));
        },
        users(){
            let me = this.$store.state.user;
            if (!me) {
                return [];
            }

            let res  = [];
            let add = (list, prop) => {
                (me[list] || []).forEach(item => {
                    let u;
                    let ru = res.find(x => x.id == item.id);
                    if (ru) {
                        u = ru;
                        Object.assign(u, item)
                    } else {
                        u = Object.assign({}, item);
                        res.push(u);
                    }

                    u[prop] = true;
                })
            }

            add('canBookFor', 'book')
            add('canManage', 'manage')
            add('managers', 'manage_me')
            add('bookers', 'book_me')
            add('paysFor', 'pay')

            res = res.filter(x => x.id != me.id).sort((a,b) => a.name.localeCompare(b.name));
            res.forEach(u => u.relationText = this.getRelationText(u));
            return res;
        },
        warnings() {
            if (this.currentUser && this.warningsRaw) {
                return this.currentUser.warnedUntil ? this.warningsRaw : this.warningsRaw.filter(x => x.deprecated == null);
            }

            return [];
        },
        canBuyBuyBlockFee() {
            return this.currentUser?.blocks.find(x => x.type == 'warning') && this.$store.state.settings.blockFeeProductId;
        }
    },
    methods: {
        getWarnings() {
            this.$api.get('/api/memberapi/warning/get', { recent: true, user_id: this.currentUser.id}).then(res => {
                this.warningsRaw = res;
            });
        },
        getRelationText(user) {
            let name = user.name.split(' ')[0];
            // FIXME: This is a Swedish hack
            let names = ['s', 'x', 'z'].includes(name[name.length-1]) ? name : name + 's';
            if (user.manage && user.manage_me) {
                return this.$translate('You manage each other\'s accounts');
            } else if (user.manage && user.book_me) {
                return this.$translate('You manage {0} account and {1} can book for you').format(names, name);
            } else if (user.manage) {
                return this.$translate('You manage {0} account').format(names);
            } else if (user.manage_me && user.book) {
                return this.$translate('{0} manages your account and you can book for {1}').format(name, name);
            } else if (user.manage_me) {
                return this.$translate('{0} manages your account').format(name);
            } else if (user.book && user.book_me) {
                return this.$translate('You can book for each other');
            } else if(user.book) {
                return this.$translate('You can book for {0}').format(name);
            } else if(user.book_me) {
                return this.$translate('{0} can book for you').format(name);
            }
        },
        getRequestText(request) {
            let name = request.requester.name.split(' ')[0];
            if (request.book && request.manage) {
                return this.$translate('{0} wants to be able to book for you and manage your account').format(name);
            } else if (request.manage) {
                return this.$translate('{0} wants to be able to manage your account').format(name);
            } else if (request.book) {
                return this.$translate('{0} wants to be able to book for you').format(name);
            }
        },
        getSentRequestText(request) {
            let name = request.user.name.split(' ')[0];
            // FIXME: This is a Swedish hack
            let names = ['s', 'x', 'z'].includes(name[name.length-1]) ? name : name + 's';
            if (request.book && request.manage) {
                return this.$translate('You want to be able to book for {0} and manage {1} account').format(name, names);
            } else if (request.manage) {
                return this.$translate('You want to be able to manage {0} account').format(names);
            } else if (request.book) {
                return this.$translate('You want to be able to book for {0}').format(name);
            }
        },
        add() {
            this.$store.commit('showAddUserDialog', {
                title: this.$translate('Add family member')
            })
        },
        change(user) {
            this.$store.commit('showChangeUserDialog', {
                user_id: user.id
            })
        },
        answer(request) {
            this.request = request;
            this.showRequestDialog = true;
        },
        acceptRequest(request) {
            this.accepting = true;
            this.$api.post('/api/memberapi/bookingrequest/answer', null, {id: request.id, answer: true}).then(() => {
                this.accepting = false;
                this.showRequestDialog = false;
                this.request = null;
            })
        },
        declineRequest(request) {
            this.declining = true;
            this.$api.post('/api/memberapi/bookingrequest/answer', null, {id: request.id, answer: false}).then(() => {
                this.declining = false;
                this.showRequestDialog = false;
                this.request = null;
            })
        },
        cancelRequest(request) {
            this.$store.commit('showConfirmDialog', {
                title: this.$translate('Remove application'),
                text: this.$translate('Are you sure you want to remove the application?'),
                actionText: this.$translate('Remove'),
                action: () => {
                    this.$api.post('/api/memberapi/bookingrequest/cancel', null, {id: request.id});
                }
            });
        },
        userHasWarningsButIsNotBlocked(user) {
            if (!this.$store.state.settings?.wbswarningsenabled) {
                return false;
            }

            if (!user.warnings ) {
                return false;
            }

            let is_blocked = !!user.blocks.find(x => x.type == 'warning');
            return !is_blocked;
        },
        getBlockMessage(block) {
            const blocktypes = BLOCK_TYPES;

            let blocklist = blocktypes.filter(t => block[t.id]).map(t => this.$translate(t.name).toLowerCase()).join(', ')
            let until = block.toDate ? (this.$translate('until') + ' <b>' + Date.formatFullShorter(block.toDate) + '</b>') : ' ' + this.$translate('until further notice');

            return this.$translate('Blocked from {0} {1}').format(blocklist, until);
        }
    },
    mounted() {
        let ar = this.$route.query.accountrequest;
        if (ar) {
            let request = this.requests.find(x => x.id == ar)
            if (request) {
                this.request = request;
                this.showRequestDialog = true;
            }
        }
    }
}
</script>
